import Cookies from "js-cookie";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from "recharts";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog/dialog";
import { usePerformanceReportsModal } from "./use-performance-reports-modal";
import moment from "moment";
import { convertUTCtoIST } from "@/utils";
import { DataTable } from "./logs-table/data-table";
import { getColumns } from "./logs-table/columns";
import { ColumnDef } from "@tanstack/react-table";
import { PerformanceLog } from "./logs-table/schema";
import {
  fetchAggregatePerformancePointsAsEmployee,
  fetchAllPerformanceLogsOfEmployeeForEmployee,
} from "@/config/queries/employee/emp-queries";
import { fetchAllPerformanceReportsForEmployeeQueryOptions } from "@/config/queries/emp-query-options";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select/select";
import { useAuth } from "@/lib/use-auth";
// import { format } from "date-fns";

const months = [
  { label: "January", value: 0 },
  { label: "February", value: 1 },
  { label: "March", value: 2 },
  { label: "April", value: 3 },
  { label: "May", value: 4 },
  { label: "June", value: 5 },
  { label: "July", value: 6 },
  { label: "August", value: 7 },
  { label: "September", value: 8 },
  { label: "October", value: 9 },
  { label: "November", value: 10 },
  { label: "December", value: 11 },
];

const monthOrder = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function generateYears(): number[] {
  const currentYear = new Date().getFullYear();
  const endYear = 2024;
  const years: number[] = [];

  for (let year = currentYear; year >= endYear; year--) {
    years.push(year);
  }

  return years;
}

const renderCustomizedLabel = (props: any) => {
  const { x, y, width, value } = props;
  const radius = 16;

  return (
    <g>
      <circle cx={x + width / 2} cy={y - radius} r={radius} fill="#8884d8" />
      <text
        x={x + width / 2}
        y={y - radius}
        fill="#fff"
        textAnchor="middle"
        dominantBaseline="middle"
      >
        {value}
      </text>
    </g>
  );
};

function getMonthDates(month: number, year: number) {
  // Moment.js starts months from 0, so we subtract 1 from the input month
  const startDate = moment([year, month]).startOf("month").toDate();
  const endDate = moment([year, month]).endOf("month").toDate();

  return {
    startDate,
    endDate,
  };
}

export const PerformanceReportsModal = () => {
  const [dateRange, setDateRange] = useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>({
    from: undefined,
    to: undefined,
  });

  const handleBarClick = (month: string, year: number) => {
    const realMonth = months.find((m) => m.label === month)?.value as number;

    const { startDate, endDate } = getMonthDates(realMonth, year);

    setDateRange({
      from: startDate,
      to: endDate,
    });
  };

  const key = Cookies.get("key") as string;
  const { open, onOpenChange } = usePerformanceReportsModal();

  const years = useMemo(() => generateYears(), []);

  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear()
  );

  const { data: UserData } = useAuth();

  console.log("user data", UserData);

  const { data, isPending } = useQuery(
    fetchAllPerformanceReportsForEmployeeQueryOptions(key, {
      year: selectedYear,
    })
  );

  const reportChartData = useMemo(() => {
    if (isPending) {
      return [];
    }

    if (data === undefined || data?.performanceReports === undefined) {
      return [];
    }

    let result = data?.performanceReports?.map((item) => {
      return {
        month: item?.month,
        year: item?.year,
        points: item?.remainingPoints,
      };
    });

    // const currentMonth = {
    //   month: format(new Date(), "MMMM"),
    //   year: new Date().getFullYear(),
    //   points: UserData?.user?.performancePoints as number,
    // };

    result = [
      ...result,
      // currentMonth
    ];

    return result?.sort((a, b) => {
      return monthOrder.indexOf(a?.month) - monthOrder.indexOf(b?.month);
    });
  }, [data, isPending]);

  const {
    data: EmployeePerformanceLogs,
    isPending: isEmpPerformanceLogsPending,
  } = useQuery({
    queryKey: ["performance-logs", dateRange],
    queryFn: () =>
      fetchAllPerformanceLogsOfEmployeeForEmployee(key)({
        startdate: convertUTCtoIST(
          new Date(
            new Date(new Date(dateRange?.from as Date).setHours(0)).setMinutes(
              0
            )
          )
        ),
        enddate: convertUTCtoIST(
          new Date(
            new Date(new Date(dateRange?.to as Date).setHours(23)).setMinutes(
              59
            )
          )
        ),
      }),
    enabled: dateRange?.from !== undefined && dateRange?.to !== undefined,
    refetchOnWindowFocus: false,
    retry: 2,
  });

  const logsData = useMemo(() => {
    if (isPending) {
      return [];
    }

    if (
      EmployeePerformanceLogs === undefined ||
      EmployeePerformanceLogs?.log === undefined
    ) {
      return [];
    }

    const result = EmployeePerformanceLogs?.log?.map((t) => {
      return {
        id: t?._id,
        deduction: t?.deduction,
        addition: t?.addition || 0,
        reasonPositive: t?.reasonPositive,
        reasonNegative: t?.reasonNegative,
        createdAt: t?.createdAt,
        date: t?.date,
      };
    });

    return result?.sort(
      (a, b) =>
        new Date(b?.createdAt).getTime() - new Date(a?.createdAt).getTime()
    );
  }, [EmployeePerformanceLogs, isEmpPerformanceLogsPending]);

  const columns = useMemo<ColumnDef<PerformanceLog, unknown>[]>(
    () => getColumns(),
    []
  );

  const [isLogDialogOpen, setIsLogDialogOpen] = useState(false);

  const { data: EmployeePerformancePoints } = useQuery({
    queryKey: ["emp-performance-agg-points"],
    queryFn: () => fetchAggregatePerformancePointsAsEmployee(key)({}),
    refetchOnWindowFocus: false,
    retry: 2,
  });

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-6xl overflow-y-scroll max-h-[650px]">
          <DialogHeader>
            <DialogTitle>Employee Performance Reports</DialogTitle>
          </DialogHeader>

          <div className="flex flex-col md:flex-row gap-3 items-center justify-between">
            <div className="">
              <h5>
                Last 3 months average score:{" "}
                <span className="font-semibold">
                  {EmployeePerformancePoints?.average?.toFixed(2)}
                </span>
              </h5>
            </div>
            <div className="self-end">
              <Select
                value={String(selectedYear)}
                onValueChange={(value) => {
                  setSelectedYear(Number(value));
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a year" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectGroup>
                    {years.map((year) => {
                      return (
                        <SelectItem key={year} value={String(year)}>
                          {year}
                        </SelectItem>
                      );
                    })}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-5 h-[500px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                width={500}
                height={300}
                data={reportChartData}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
                barSize={40}
              >
                <XAxis
                  dataKey="month"
                  scale="point"
                  padding={{ left: 40, right: 40 }}
                />
                <YAxis
                  padding={{
                    top: 40,
                  }}
                />
                <Tooltip />
                <Legend />
                <CartesianGrid strokeDasharray="3 3" />
                <Bar
                  dataKey="points"
                  fill="#8884d8"
                  background={{ fill: "#eee" }}
                  onClick={(data, _) => {
                    const month = data?.month as string;
                    const year = data?.year as number;

                    setIsLogDialogOpen(true);
                    handleBarClick(month, year);
                  }}
                >
                  <LabelList dataKey="points" content={renderCustomizedLabel} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isLogDialogOpen}
        onOpenChange={(value) => {
          if (!value) {
            setDateRange({
              from: undefined,
              to: undefined,
            });
          }

          setIsLogDialogOpen(value);
        }}
      >
        <DialogContent className="max-w-7xl overflow-y-scroll max-h-[580px]">
          <DialogHeader>
            <DialogTitle>Employee Performance Logs</DialogTitle>
          </DialogHeader>

          <div className="mt-5">
            <DataTable data={logsData} columns={columns} />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
